import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Building2, Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@accio3d/design-system/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@accio3d/design-system/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@accio3d/design-system/form';
import { Input } from '@accio3d/design-system/input';
import { cn } from '@accio3d/design-system/utils';

import { useAuth } from '../../hooks/useAuth';
import { OrgAudience } from '../../guards/permissions';
import { OrganizationTypePanels } from '../organization-type-panels';

interface OrganizationPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const addOrgSchema = z.object({
  name: z.string().trim().min(2, 'Name must be at least 2 characters'),
  type: z.enum([OrgAudience.PURCHASER, OrgAudience.SUPPLIER]),
});

type AddOrgFormValues = z.infer<typeof addOrgSchema>;

function AddOrgInlineForm({ onSuccess }: { onSuccess: () => void }) {
  const { registerOrganization } = useAuth();
  const form = useForm<AddOrgFormValues>({
    resolver: zodResolver(addOrgSchema),
    defaultValues: { name: '', type: OrgAudience.PURCHASER },
  });

  const onSubmit = async (values: AddOrgFormValues) => {
    try {
      await registerOrganization({
        name: values.name.trim(),
        type: values.type,
      });
      toast.success('Organization created.');
      onSuccess();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not create organization');
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="space-y-1">
              <FormLabel className="text-sm font-medium">Organization name</FormLabel>
              <FormControl>
                <Input
                  className="h-10 rounded-lg text-sm"
                  placeholder="Acme Manufacturing"
                  disabled={form.formState.isSubmitting}
                  {...field}
                />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem className="space-y-1">
              <FormLabel className="text-sm font-medium">Organization type</FormLabel>
              <FormControl>
                <OrganizationTypePanels
                  value={field.value}
                  onChange={field.onChange}
                  disabled={form.formState.isSubmitting}
                />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? 'Creating...' : 'Create Organization'}
        </Button>
      </form>
    </Form>
  );
}

// Mirrors the portals' shared-ui OrganizationPickerModal: list memberships to
// switch between, plus an inline "create organization" form.
export function OrganizationPickerModal({ open, onOpenChange }: OrganizationPickerModalProps) {
  const { availableOrganizations, organization, switchOrganization } = useAuth();
  const [isSwitching, setIsSwitching] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  const currentOrgId = organization?.id;

  const handleSelectOrg = async (orgId: string) => {
    if (isSwitching) return;
    if (orgId === currentOrgId) {
      onOpenChange(false);
      return;
    }
    setIsSwitching(true);
    try {
      await switchOrganization(orgId);
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to switch');
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-lg">
            {showAddForm ? 'Create Organization' : 'Select Organization'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!showAddForm ? (
            <>
              <div className="max-h-[50vh] space-y-3 overflow-y-auto py-1">
                {availableOrganizations.map((org) => (
                  <div
                    key={org.membershipId}
                    className={cn(
                      'group relative flex items-center justify-between rounded-lg border p-4 transition-all',
                      isSwitching
                        ? 'cursor-not-allowed opacity-60'
                        : 'cursor-pointer border-border hover:border-foreground/40 hover:bg-muted/50',
                      org.id === currentOrgId && 'border-primary bg-muted',
                    )}
                    onClick={() => handleSelectOrg(org.id)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{org.name}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="capitalize">{org.type}</span>
                        <span>•</span>
                        <span className="capitalize">{org.role}</span>
                      </div>
                    </div>
                    {org.id === currentOrgId && (
                      <span className="text-xs font-medium text-primary">Current</span>
                    )}
                  </div>
                ))}
              </div>

              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-background px-2 text-xs uppercase text-muted-foreground">
                    Or
                  </span>
                </div>
              </div>

              <Button variant="outline" className="w-full" onClick={() => setShowAddForm(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create New Organization
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAddForm(false)}
                className="mb-2"
              >
                ← Back to list
              </Button>
              <AddOrgInlineForm onSuccess={() => setShowAddForm(false)} />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
